import React, { useEffect, useRef, useState, ReactNode } from 'react';
import _ from 'lodash';

// MUI
import { Accordion, AccordionDetails, AccordionSummary, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField } from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '../../../assets/images/search.svg';
import SearchBtnIcon from '../../../assets/images/search-btn.svg';
import RefreshBtnIcon from '../../../assets/images/refresh-btn.svg';

// Utils
import { Storage } from '../../../utils/Storage';

// Types
import { FilterableTypes } from '../../../types/FilterableTypes';

import './SearchBar.scss';

interface SearchBarProp {
  pageKey?: string;
  value?: string | number;
  onSearch: (filters: any[]) => void;
  onReset?: () => void;
  allowSearchAll?: boolean;
  defaultSearchColumn?: string;
  multiFilterType?: string;
  columns: any[];
  components?: ReactNode;
  // When true, renders only `components` (no text search field, column select, or search/reset buttons).
  hideSearchInput?: boolean;
}

const SearchBar = (props: SearchBarProp) => {
  const [columnSearch, setColumnSearch] = useState(Storage.getSearchBarFiltering(props.pageKey)?.column ?? '');

  const [value, setValue] = useState(Storage.getSearchBarFiltering(props.pageKey)?.value ?? '');

  const [expand, setExpand] = useState(true);

  const formRef = useRef<HTMLFormElement>(null);

  const setDefaultFilter = (isReset = false) => {
    const col = _.first(props.columns.filter((v) => v.enableColumnFilter));

    const defaultColumn = props.defaultSearchColumn ?? 'all';

    if (col && !props.allowSearchAll) {
      setColumnSearch(columnSearch ? columnSearch : (props.defaultSearchColumn ?? col.accessorKey));
    } else {
      if (isReset) {
        setColumnSearch(defaultColumn);
      } else {
        setColumnSearch(columnSearch ? columnSearch : defaultColumn);
      }
    }
  };

  useEffect(() => {
    if (props.columns) {
      setDefaultFilter();
    }
  }, [props.columns]);

  const handleChange = (event: any) => {
    setColumnSearch(event.target.value);
  };

  const handleReset = () => {
    Storage.clearSearchBarFiltering(props.pageKey);

    if (props.columns) {
      setDefaultFilter(true);
    }

    props.onReset?.();

    setValue('');
    props.onSearch([]);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const searchValue = String(value ?? '');

    let filters: any;

    if (columnSearch === 'all' && searchValue) {
      filters = props.columns
        .filter((v) => v.enableColumnFilter)
        .map((c) => ({
          [c.accessorKey]: searchValue,
        }));
    } else {
      filters = searchValue ? [{ [columnSearch]: searchValue }] : [];
    }

    const filterParams = buildSearchFiltering(props.multiFilterType ?? FilterableTypes.Contains, filters);

    if (searchValue) {
      Storage.setSearchBarFiltering(props.pageKey, {
        value: searchValue,
        column: columnSearch,
        isSearchBar: true,
        filters: filterParams,
      });
    }

    props.onSearch(filterParams);
  };

  const buildSearchFiltering = (filterType: string, filters: any[]) => {
    if (filters.length > 1) {
      const nestedFilters = filters.map((filter) => {
        const [key] = Object.keys(filter);
        const filterValue = filter[key];

        return {
          [key]: filterValue,
          filterType,
        };
      });

      const result = nestedFilters.reduceRight((acc, curr) => {
        return {
          ...curr,
          or: acc,
        };
      });

      return [result];
    }

    return filters;
  };

  const toggleAccordion = () => {
    setExpand((prev) => !prev);
  };

  if (props.hideSearchInput) {
    return props.components ? <div className='search-bar__filters'>{props.components}</div> : null;
  }

  return (
    <form ref={formRef} className='search-bar' onSubmit={handleSubmit}>
      <Accordion expanded={expand} className='search-bar__accordion' disableGutters>
        <AccordionSummary
          className='search-bar__summary'
          expandIcon={
            props.components ? (
              <IconButton
                className='search-bar__expand-button'
                onClick={(event) => {
                  event.stopPropagation();
                  toggleAccordion();
                }}
                aria-label={expand ? 'Collapse filters' : 'Expand filters'}
                title={expand ? 'Collapse filters' : 'More filters'}
              >
                <ExpandMoreIcon />
              </IconButton>
            ) : null
          }
          sx={{
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <div className='search-bar__main'>
            <TextField
              className='search-bar__input'
              size='small'
              id='searchInput'
              placeholder='Filter...'
              value={value ?? ''}
              onChange={(event) => {
                setValue(event.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <img src={SearchIcon} alt='search-icon' className='search-bar__input-icon' />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl className='search-bar__select-control' size='small'>
              <Select variant='outlined' id='searchColumn' value={columnSearch} onChange={handleChange} className='search-bar__select' displayEmpty>
                {props.allowSearchAll && <MenuItem value='all'>All</MenuItem>}

                {props.columns
                  .filter((v) => v.enableColumnFilter)
                  .map((column, index) => (
                    <MenuItem key={index} value={column.accessorKey}>
                      {typeof column.header === 'string' ? column.header : (column.header?.label ?? column.accessorKey)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <IconButton type='submit' className='search-bar__action-button' aria-label='Search' title='Search'>
              <img src={SearchBtnIcon} alt='search-icon' className='search-bar__input-icon' />
            </IconButton>

            <IconButton type='button' className='search-bar__action-button' aria-label='Reset' title='Reset' onClick={handleReset}>
              <img src={RefreshBtnIcon} alt='refresh-icon' className='search-bar__input-icon' />
            </IconButton>

            {!props.components && (
              <IconButton
                type='button'
                className='search-bar__action-button'
                onClick={toggleAccordion}
                aria-label={expand ? 'Collapse filters' : 'Expand filters'}
              >
                <ExpandMoreIcon className={expand ? 'search-bar__expand-icon search-bar__expand-icon--expanded' : 'search-bar__expand-icon'} />
              </IconButton>
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails className='search-bar__details'>
          {props.components && <div className='search-bar__filters'>{props.components}</div>}
        </AccordionDetails>
      </Accordion>
    </form>
  );
};

export default SearchBar;
